/**
 * ActorMotivationProfilePanel — the actor edit dialog's "Motivation Profile"
 * section. Reads the latest stored Simonov motivation profile for one actor,
 * lets the operator GENERATE/REGENERATE it (deterministic engine, via the agent
 * endpoint — the same path the CLI `internal actors motivation derive` drives),
 * and EDIT the per-need weights (persisted as a manual override, source='manual').
 *
 * Edit-wins: regenerate over a manual profile asks for confirmation first (the
 * backend returns the actor under `warnings` unless force:true).
 *
 * Self-contained: reads the latest version straight from Firestore
 * (projects/{pid}/audiences/{aid}/actors/{actorId}/motivationProfile/{version}).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

const VALENCES = ['preservation', 'growth'];

/** Human group label from a need-id prefix (matches the engine's NeedGroup). */
function groupOf(needId) {
  if (needId.startsWith('vital-')) return 'Vital';
  if (needId.startsWith('social-')) return 'Social';
  if (needId.startsWith('ideal-')) return 'Ideal';
  if (needId.startsWith('competence-')) return 'Competence';
  if (needId.startsWith('overcoming-')) return 'Overcoming';
  return '—';
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function ActorMotivationProfilePanel({ projectId, audienceId, actorId, actorName }) {
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draftNeeds, setDraftNeeds] = useState(null); // { [needId]: { preservation, growth } }
  const [pendingOverwrite, setPendingOverwrite] = useState(false);

  const loadLatest = useCallback(async () => {
    if (!projectId || !audienceId || !actorId) return;
    setLoading(true);
    setError('');
    try {
      const db = CoreFirebaseService.getInstance().firestore;
      const ref = collection(
        db,
        `projects/${projectId}/audiences/${audienceId}/actors/${actorId}/motivationProfile`,
      );
      const snap = await getDocs(query(ref, orderBy('version', 'desc'), limit(1)));
      setRecord(snap.empty ? null : snap.docs[0].data());
    } catch (e) {
      setError(e?.message || 'Failed to load motivation profile');
    } finally {
      setLoading(false);
    }
  }, [projectId, audienceId, actorId]);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  const generate = useCallback(async (force) => {
    setBusy(true);
    setError('');
    setPendingOverwrite(false);
    try {
      const api = new AgentApiClient();
      const res = await api.deriveMotivation({ projectId, audienceId, actorId, persist: true, force });
      if (!res.success) throw new Error(res.error || 'Derivation failed');
      const skipped = (res.warnings || []).some((w) => w.actorId === actorId && w.reason === 'manual_exists');
      if (skipped && !force) {
        setPendingOverwrite(true); // manual profile exists — ask before overwrite
        return;
      }
      await loadLatest();
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Derivation failed');
    } finally {
      setBusy(false);
    }
  }, [projectId, audienceId, actorId, loadLatest]);

  const startEdit = useCallback(() => {
    const needs = record?.profile?.needs || {};
    // deep-clone the per-need valence weights into an editable draft.
    // Round to 2 decimals for a readable input (these are hand-authored
    // overrides — full-precision floats like 2.3684210526… are unusable).
    const editVal = (n) =>
      n === undefined || n === null || n === '' ? '' : Math.round(Number(n) * 100) / 100;
    const draft = {};
    for (const [needId, valences] of Object.entries(needs)) {
      draft[needId] = {
        preservation: editVal(valences?.preservation),
        growth: editVal(valences?.growth),
      };
    }
    setDraftNeeds(draft);
    setEditing(true);
  }, [record]);

  const saveEdit = useCallback(async () => {
    if (!record?.profile) return;
    setBusy(true);
    setError('');
    try {
      // reconstruct profile.needs from the draft (drop empty/zero valences)
      const nextNeeds = {};
      for (const [needId, valences] of Object.entries(draftNeeds || {})) {
        const entry = {};
        for (const v of VALENCES) {
          const raw = valences[v];
          if (raw !== '' && raw !== null && raw !== undefined && Number(raw) !== 0) {
            entry[v] = Number(raw);
          }
        }
        if (Object.keys(entry).length > 0) nextNeeds[needId] = entry;
      }
      const nextProfile = { ...record.profile, needs: nextNeeds };
      const api = new AgentApiClient();
      const res = await api.saveMotivationProfile({
        projectId, audienceId, actorId, profile: nextProfile, actorName,
      });
      if (!res.success) throw new Error(res.error || 'Save failed');
      await loadLatest();
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [record, draftNeeds, projectId, audienceId, actorId, actorName, loadLatest]);

  // ── render ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const summary = record?.summary;
  const isManual = record?.source === 'manual';

  return (
    <Box data-testid="actor-motivation-section" sx={{ mt: 1 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>Motivation Profile</Typography>
        {record && (
          <Tooltip title={`derived ${record.derivedAt ? new Date(record.derivedAt).toLocaleString() : ''}`}>
            <Chip
              size="small"
              data-testid="actor-motivation-source-badge"
              color={record.source === 'manual' ? 'secondary' : record.source === 'agent' ? 'info' : 'primary'}
              label={`${record.source} · v${record.version}`}
            />
          </Tooltip>
        )}
        {record && !editing && (
          <>
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              onClick={() => generate(false)}
              disabled={busy}
              data-testid="actor-motivation-regenerate-btn"
              sx={{ textTransform: 'none' }}
            >
              Regenerate
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={startEdit}
              disabled={busy}
              data-testid="actor-motivation-edit-btn"
              sx={{ textTransform: 'none' }}
            >
              Edit
            </Button>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" data-testid="actor-motivation-error" sx={{ mb: 1, fontSize: '0.85rem' }}>
          {error}
        </Alert>
      )}

      {pendingOverwrite && (
        <Alert
          severity="warning"
          data-testid="actor-motivation-overwrite-warning"
          sx={{ mb: 1, fontSize: '0.85rem' }}
          action={
            <Button color="inherit" size="small" onClick={() => generate(true)} disabled={busy}>
              Overwrite
            </Button>
          }
        >
          This actor has a manual profile. Regenerating will replace it with a freshly derived one.
        </Alert>
      )}

      {/* Empty state */}
      {!record && !pendingOverwrite && (
        <Box data-testid="actor-motivation-empty" sx={{ py: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            No motivation profile yet — derive the actor's Simonov needs from its JTBD.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
            onClick={() => generate(false)}
            disabled={busy}
            data-testid="actor-motivation-generate-btn"
            sx={{ textTransform: 'none' }}
          >
            Generate profile
          </Button>
        </Box>
      )}

      {/* Summary + needs table */}
      {record && summary && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
            <Box data-testid="actor-motivation-dominant-need">
              <Typography variant="caption" color="text.secondary">Dominant need</Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  color={summary.dominantNeed?.valence === 'growth' ? 'success' : 'info'}
                  label={summary.dominantNeed?.needId || '—'}
                />
                <Typography variant="caption" color="text.secondary">
                  {summary.dominantNeed?.valence} · {round2(summary.dominantNeed?.strength)}
                </Typography>
              </Stack>
            </Box>
            <Box data-testid="actor-motivation-initial-motivation">
              <Typography variant="caption" color="text.secondary">Initial motivation</Typography>
              <Typography variant="body2">{round2(summary.initialMotivation)}</Typography>
            </Box>
          </Stack>

          <TableContainer data-testid="actor-motivation-needs-table">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Need</TableCell>
                  <TableCell align="right">Preservation</TableCell>
                  <TableCell align="right">Growth</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries((editing ? draftNeeds : record.profile?.needs) || {}).map(([needId, valences]) => {
                  const p = editing ? valences.preservation : (valences?.preservation ?? 0);
                  const g = editing ? valences.growth : (valences?.growth ?? 0);
                  const total = round2((Number(p) || 0) + (Number(g) || 0));
                  return (
                    <TableRow key={needId}>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{groupOf(needId)}</Typography>
                        <Typography variant="body2">{needId}</Typography>
                      </TableCell>
                      {VALENCES.map((v) => (
                        <TableCell key={v} align="right">
                          {editing ? (
                            <TextField
                              type="number"
                              size="small"
                              variant="standard"
                              value={draftNeeds[needId][v]}
                              onChange={(e) =>
                                setDraftNeeds((prev) => ({
                                  ...prev,
                                  [needId]: { ...prev[needId], [v]: e.target.value },
                                }))
                              }
                              inputProps={{
                                step: 0.05,
                                min: 0,
                                style: { textAlign: 'right', width: 76 },
                                'data-testid': `actor-motivation-need-${needId}-${v}`,
                              }}
                            />
                          ) : (
                            round2(v === 'preservation' ? p : g)
                          )}
                        </TableCell>
                      ))}
                      <TableCell align="right">{total}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {editing && (
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, alignSelf: 'center' }}>
                Saving stores a manual override (dominant need is recomputed).
              </Typography>
              <Button
                size="small"
                onClick={() => { setEditing(false); setDraftNeeds(null); }}
                disabled={busy}
                data-testid="actor-motivation-cancel-btn"
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={saveEdit}
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
                data-testid="actor-motivation-save-btn"
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}
